import React from 'react';
import LanguageSwitcher from '../LanguageSwitcher';

const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtEUR = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const fmtHUF = (n) => `${Math.round(Number(n) || 0).toLocaleString('hu-HU')} Ft`;
const fmtDate = (ms) => ms ? new Date(ms).toLocaleDateString() : '—';

export default function Portal({ t, lang, setLang, data, email, onLogout }) {
  const { partners, summary, monthly, conversions, payouts } = data;
  const maxComm = Math.max(1, ...monthly.map(m => m.commissionUSD));

  return (
    <div className="wrap">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="h1">🤝 {t('portal')}</div>
          <div className="muted">{email}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <LanguageSwitcher lang={lang} onChange={setLang} />
          <button className="btn-ghost" onClick={onLogout}>{t('logout')}</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="title">{t('account')}</div>
        {partners.map(p => (
          <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ color: '#00d4ff', fontFamily: 'monospace', fontWeight: 700 }}>{p.code}</span>
              <span style={{ color: '#e4e6eb' }}> · {p.name}</span>
            </div>
            <div className="muted">{t('commission')}: {(p.commissionRate * 100).toFixed(0)}% · {p.active ? t('active') : t('inactive')}</div>
          </div>
        ))}
      </div>

      <div className="grid" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="title">{t('conversions')}</div>
          <div className="big">{summary.conversions}</div>
          <div className="muted">{t('refunded')}: {fmtUSD(summary.refundedCommissionUSD)}</div>
        </div>
        <div className="card">
          <div className="title">{t('cumCommission')}</div>
          <div className="big">{fmtUSD(summary.earnedUSD)}</div>
          <div className="muted">{fmtEUR(summary.earnedEUR)} · {fmtHUF(summary.earnedHUF)}</div>
        </div>
        <div className="card">
          <div className="title">{t('payable')}</div>
          <div className="big" style={{ color: '#fbbf24' }}>{fmtHUF(summary.pendingHUF)}</div>
          <div className="muted">{fmtEUR(summary.pendingEUR)} · {fmtUSD(summary.pendingUSD)}</div>
        </div>
        <div className="card">
          <div className="title">{t('paidOut')}</div>
          <div className="big" style={{ color: '#86efac' }}>{fmtUSD(summary.paidUSD)}</div>
          <div className="muted">{fmtEUR(summary.paidEUR)} · {fmtHUF(summary.paidHUF)}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="title">{t('monthlyCommission')}</div>
        {monthly.length === 0 ? <div className="muted">{t('noData')}</div> : monthly.map(m => (
          <div key={m.month} className="bar-row">
            <span style={{ width: 64, color: '#8b8fa3' }}>{m.month}</span>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${(m.commissionUSD / maxComm) * 100}%` }} /></div>
            <span style={{ width: 110, textAlign: 'right' }}>{fmtUSD(m.commissionUSD)} · {m.conversions}</span>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="title">{t('conversionsRecent')} {conversions.length})</div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>{t('date')}</th><th>{t('product')}</th><th>{t('platform')}</th>
                <th style={{ textAlign: 'right' }}>{t('gross')}</th>
                <th style={{ textAlign: 'right' }}>{t('commission')}</th>
                <th style={{ textAlign: 'center' }}>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {conversions.map(c => (
                <tr key={c.id} style={{ opacity: c.refunded ? 0.5 : 1 }}>
                  <td>{fmtDate(c.purchaseAt)}</td>
                  <td style={{ fontFamily: 'monospace' }}>{c.productId}</td>
                  <td>{c.platform}</td>
                  <td style={{ textAlign: 'right' }}>{fmtUSD(c.grossUSD)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtUSD(c.commissionUSD)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`tag ${c.refunded ? 'tag-no' : 'tag-ok'}`}>{c.refunded ? t('refunded') : t('ok')}</span>
                  </td>
                </tr>
              ))}
              {conversions.length === 0 && <tr><td colSpan={6} className="muted">{t('noConversions')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="title">{t('payoutHistory')}</div>
        {payouts.length === 0 ? <div className="muted">{t('noPayouts')}</div> : (
          <table>
            <thead><tr><th>{t('date')}</th><th>{t('note')}</th><th style={{ textAlign: 'right' }}>{t('amount')}</th></tr></thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.id}>
                  <td>{fmtDate(p.paidAt)}</td>
                  <td className="muted">{p.note || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtUSD(p.amountUSD)} · {fmtHUF(p.amountHUF)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="muted" style={{ textAlign: 'center', marginTop: 24, fontSize: 11 }}>{t('footer')}</div>
    </div>
  );
}
